<script setup>

import { cityDisplayName, isMobileView, l } from '@utils/toolbox';

const props = defineProps({
	emptyLabel: String,
	label: String,
	config: String,
	searchable: Boolean,
	initialValue: {
		type: Object,
		required: true
	}
});

const items = defineModel('items', {
	type: [Array, Object],
	required: true
});

const selected = defineModel('selected', {
	type: Array,
	required: true
});

const {
	convert,
	emptyLabel,
	hint
} = useLastMinuteSelect(props.config);

const itemsConverted = computed(() => {
	return convert(items.value);
});

// Set initial values
const isMatch = (val) => {
	if (Array.isArray(val)) {
		return val.some(v => props.initialValue.to?.includes(v));
	}
	return props.initialValue.to?.includes(val);
};

// Loop over itemsConverted to find matching values from props.initialValue
function getValues () {
	const resp = itemsConverted.value.flatMap((item) => {
		if (item?.value) {
			return isMatch(item.text) ? [item] : [];
		}

		return item.values.filter(subItem => isMatch(subItem.value));
	});
	return resp;
}

Object.assign(selected.value, getValues());

function useLastMinuteSelect (config) {
	switch (config) {
		case 'searchParamsFromIata':
			return {
				emptyLabel: isMobileView() ? 'global.simplePhrases.all' : 'lastminutePage.filterBox.allDepartures',
				hint: 'global.simplePhrases.From',
				// Go from array of strings to array of objects
				convert (data) {
					// Convert all Cities[n].City to Cities[n].text and value
					return Object.values(data).map((item) => {
						return item?.SubIatas?.length
							? { text: item.Name, values: item.SubIatas.map(city => ({ text: city.Name, value: city.IATA })) }
							: { text: item.Name, value: item.IATA };
					});
				}
			};
		case 'searchParamsToIata':
			return {
				emptyLabel: 'lastminutePage.filterBox.toWholeWorld',
				hint: 'global.simplePhrases.To',
				convert (data) {
					return Object.entries(data).map(([country, cities]) => ({
						text: country,
						values: Object.entries(cities).map(([cityName, iatas]) => ({
							text: cityDisplayName(cityName),
							value: iatas
						}))
					}));
				}
			};

		case 'searchParamsLeaveDate':
			return {
				emptyLabel: 'lastminutePage.filterBox.leaveWhenever',
				hint: 'global.simplePhrases.outboundJourneyDate',
				convert (data) {
					return [{
						text: l('lastminutePage.filterBox.leaveAll'),
						values: Object.entries(data).map(([value, text]) => {
							return { text, value };
						})
					}];
				}
			};

		case 'searchParamsTripLength':
			return {
				emptyLabel: 'lastminutePage.filterBox.tripLengthAll',
				hint: 'lastminutePage.list.triplength',
				convert (data) {
					return [{
						text: l('lastminutePage.filterBox.tripLengthAll'),
						values: Object.entries(data).map(([value, text]) => {
							return { text, value };
						})
					}];
				}
			};
	}
}

</script>

<template>
  <BaseCombo
    :key="config"
    v-model:selected="selected"
    :items="itemsConverted"
    :searchable="searchable"
  >
    <template #activator="{ toggleFocus, isFocused, toggleItem }">
      <div class="SearchFieldMulti__wrapper--activator" @click="toggleFocus">
        <label class="SearchFieldMulti__label" for="#">
          {{ $l(hint) }}
        </label>
        <div class="abInputMulti__btn">
          <div class="inputMulti__btn--text">
            <span v-if="!selected.length">{{ $l(emptyLabel) }}</span>
            <span
              v-for="item in selected"
              :key="item.value"
              class="inputMulti__chip"
              @click.stop="toggleItem(item)"
            >
              {{ item.text }}
              <div class="chip__remove">x</div>
            </span>
          </div>
        </div>
      </div>
    </template>
  </BaseCombo>
</template>


<style lang="scss">
.inputMulti__btn--text {
  overflow-x: auto;
  overflow-y: hidden;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding-bottom: 0.2rem;
  padding-top: 0.3rem;
  scrollbar-width: thin;
}

.inputMulti__chip {
	font-size: 2rem;
	line-height: 2.2rem;
	font-weight: 600;
	padding: .1rem .5rem;
	color: #2e5b8b;
	white-space: nowrap;
	margin-right: 1rem;
	border-radius: .4rem;
	border: .1rem #2E5B8B solid;
	cursor: pointer;
	display: inline-flex;
	align-items: center;

	.chip__remove {
		background: none;
		border: none;
		color: $color-blue-800;
		margin-left: 0.4rem;
		cursor: pointer;
		font-size: 1.2rem;
		line-height: 1;
	}
}

#multi-select-adults, #multi-select-triplength {
	width: 50%;
	flex: 50% 1 1;
	max-width: calc(50% - 0.5rem);
	height: 6rem;
	@include breakpoint_up(md) {
		max-width: calc(33.33% - .67rem);
		flex: 50% 1 1;
	}
}

#multi-select-leave,
#multi-select-triplength {
	width: 100%;
	height: 6rem;
	@include breakpoint_up(md) {
		max-width: calc(33.33% - .67rem);
		flex: 50% 1 1;
	}
}

#multi-select-from,
#multi-select-to {
	width: 50%;
	flex: 50% 1 1;
	max-width: calc(50% - 0.5rem);
	height: 6rem;
}


.SearchFieldMulti__wrapper--activator {
	padding: 1rem 2rem;
	display: block;
	border-radius: 0.4rem;
	background: $color-blue-000;
	height: 6rem;

	@include breakpoint_down(md) {
		flex: 100% 1 1;
		width: 100%;
		gap: 1rem;
	}

	&.active {
		z-index: $z-Search
	}
}
</style>

<style lang="scss">
.SearchFieldMulti__wrapper {
	display: flex;
	justify-content: space-between;
	flex-direction: column;
	flex: calc(20% - 1rem) 1 0;
	max-width: calc(25% - 1.16rem);
	height: 6.5rem;
	background: $white;
	border-radius: $borderRadius-xs;

	@include breakpoint_down(md) {
		flex: 100% 1 1;
		max-width: 100%;
	}
}

.SearchFieldMulti__label {
	font-size: 1.4rem;
	pointer-events: none;
	color: $color-grey-500;
}

.abInputMultiWrapper {
	position: relative;
	overflow: hidden;
	min-height: 2.5rem;
}

.abInputMultiWrapper--active {
	width: 100%;
	height: 100%;
	background-color: $white;
	overflow: visible;

	.abInputMulti__dropdown {
		display: block;
		min-width: 20rem;

		@include breakpoint_down(md) {
			position: fixed;
			left: 0;
			right: 0;
			top: 0;
			width: 100%;
			height: 100%;
		}
	}
}

.abInputMulti__btn {
	outline: none;
	border: 0;
	margin: 0;
	padding: 0;
	width: 100%;
	text-align: left;
	background: $color-blue-000;

	font-size: 2rem;
	line-height: 2.8rem;
	font-weight: 600;
	color: $color-blue-800;
	white-space: nowrap;
}

.abInputMulti__btn--text {
	overflow: hidden;
	display: block;
	position: relative;
	white-space: nowrap;
	text-overflow: ellipsis;
}

.abInputMulti__dropdown {
	z-index: 9999;
	display: none;
	position: absolute;
	left: -1rem;
	// top: 3rem;
	width: calc(100% + 7rem);
	border-radius: 0 0 $borderRadius-sm $borderRadius-sm;
	background-color: $white;
	overflow: hidden;
}
</style>
